import { ToastrService } from 'ngx-toastr';

import { Component, OnInit, Inject } from '@angular/core';

import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup, FormControl, FormBuilder, Validators } from '@angular/forms';

import { AngularFirestore } from '@angular/fire/compat/firestore';

import { SpinnerService } from '../../services/spinner.service';
import { ApiNotice, ApiSetNoticesRequest } from '../../api-client-module';
import { Project } from '../../firestore-models/project';

import { ApiClientService } from '../../api-client.service';
import { AppConst } from '../../app-const';
import { SelectProjectDialogComponent } from '../../select-project-dialog/select-project-dialog.component';
import { TranslateService } from '@ngx-translate/core';

export interface NoticeManagementDialogData {
  change_type: number;
  notice?: ApiNotice;
}

@Component({
  selector: 'app-notice-management-dialog',
  templateUrl: './notice-management-dialog.component.html',
  styleUrls: ['./notice-management-dialog.component.css'],
  standalone: false,
})
export class NoticeManagementDialogComponent implements OnInit {

  // 通知情報
  notice?: ApiNotice;

  isAdded: boolean = false;

  // 通知情報フォーム
  noticeFormGroup!: FormGroup;
  noticeTitleControl!: FormControl;
  noticeDetailControl!: FormControl;
  noticeTypeControl!: FormControl;

  noticeTypes: any = [
    { type: 'info', name: this.translate.instant('情報') },
    { type: 'warn', name: this.translate.instant('ワーニング') },
    { type: 'err', name: this.translate.instant('エラー') }
  ]

  projects: Project[] = [];

  constructor(
    public dialogRef: MatDialogRef<NoticeManagementDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: NoticeManagementDialogData,
    public dialog: MatDialog,
    private formBuilder: FormBuilder,
    private angularFirestore: AngularFirestore,
    private translate: TranslateService,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
  ) {
  }

  ngOnInit(): void {
    this.notice = this.data.notice;
    if (!this.notice) {
      this.notice = {
        id: '',
        project_ids: [],
        notice_title: '',
        notice_detail: '',
        notice_type: '',
        confirmed: [],
      };
      this.isAdded = true;
    }

    this.createForm();
    this.noticeTitleControl = this.noticeFormGroup.get('noticeTitle') as FormControl;
    this.noticeDetailControl = this.noticeFormGroup.get('noticeDetail') as FormControl;
    this.noticeTypeControl = this.noticeFormGroup.get('noticeType') as FormControl;

    this.getProjects();
  }

  // 以下、フォームの記入エラー表示
  public getErrorMessageToNoticeTitle() {
    return this.noticeTitleControl.hasError('required')
      ? this.translate.instant('必須項目')
      : '';
  }

  public getErrorMessageToNoticeDetail() {
    return this.noticeDetailControl.hasError('required')
      ? this.translate.instant('必須項目')
      : '';
  }

  // フォームの値を各要素に代入
  private createForm() {
    this.noticeFormGroup = this.formBuilder.group(
      {
        noticeType: [this.notice!.notice_type, [Validators.required]],
        noticeTitle: [this.notice!.notice_title, [Validators.required]],
        noticeDetail: [this.notice!.notice_detail, [Validators.required]],
      }
    );
  }

  // プロジェクト設定
  private getProjects() {
    this.projects = [];
    if (!!this.notice!.project_ids && this.notice!.project_ids.length > 0) {
      this.angularFirestore.collection<Project>(
        AppConst.FIRESTORE_PROJECTS).valueChanges({ idField: 'id' }).subscribe(docs => {
          this.projects = [];
          if (!!docs) {
            for (const projectId of this.notice!.project_ids!) {
              for (const doc of docs) {
                if (projectId === doc.id) {
                  this.projects.push(doc);
                  break;
                }
              }
            }
          }
        });
    }
  }

  // 更新ボタン
  addNotice(): void {
    if (this.noticeFormGroup.valid) {
      this.notice!.notice_type = this.noticeTypeControl.value;
      this.notice!.notice_title = this.noticeTitleControl.value;
      this.notice!.notice_detail = this.noticeDetailControl.value;
      if (this.notice) {
        let request: ApiSetNoticesRequest = {
          access_token: '',
          change_type: this.data.change_type,
          notices: [this.notice]
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setNotices(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('登録しました。'));
              this.dialogRef.close();
            } else if (response!.ret === AppConst.API_RET_ALREADY_EXIST_DATA) {
              this.toastr.error(this.translate.instant('既に同じIDが登録済みです。'), '', { timeOut : 5000 });
            } else {
              console.error('api update error. ret='+response!.ret);
              this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
          }
        );
      }
    }
  }

  // プロジェクト選択ダイアログ
  openSelectProjectDialog(): void {
    const dialogRef = this.dialog.open(SelectProjectDialogComponent, {
      width: '80%',
      disableClose: true,
      data: { projects: null }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result && result.length > 0) {
        if (!this.notice!.project_ids) {
          this.notice!.project_ids = new Array<string>();
        }
        let isChanged: boolean = false;
        for (const row of result) {
          if (!this.notice!.project_ids.includes(row.id)) {
            this.notice!.project_ids.push(row.id);
            isChanged = true;
          }
        }
        if (!!isChanged) {
          this.getProjects();
        }
      }
    });
  }

  deleteProject(id: string): void {
    if (!!this.notice!.project_ids!.includes(id)) {
      this.notice!.project_ids = this.notice!.project_ids!.filter(value => value !== id);
      this.getProjects();
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

}
